import { InputNumber, Table, Tag, type TableProps } from 'antd'
import type { z } from 'zod'
import type { billSchema } from '@super-mega/server/contracts/bill'
import { BusinessTag } from '@/features/master-data/index.ts'
import { formatAmount } from '@/utils/money.ts'
import { BillUtilityFields } from './bill-utility-fields.tsx'
import { BILL_KIND_COLORS, BILL_KINDS } from './const/bill-search.ts'
import { canEnterUtility, initialIssueEntry, issueEntryAmount, type BillIssueEntry } from './utils/bill-issue.ts'

type Bill = z.infer<typeof billSchema>

export function BillConfirmationTable({
  bills,
  issue = false,
  editAmount = false,
  entries = {},
  onEntryChange,
  current,
  onPageChange,
  disabled,
  amountColumn,
  extraColumns = []
}: {
  bills: Bill[]
  issue?: boolean
  editAmount?: boolean
  entries?: Partial<Record<number, BillIssueEntry>>
  onEntryChange?: (id: number, entry: BillIssueEntry) => void
  current: number
  onPageChange: (page: number) => void
  disabled: boolean
  amountColumn?: NonNullable<TableProps<Bill>['columns']>[number]
  extraColumns?: TableProps<Bill>['columns']
}) {
  const showMetering = issue && bills.some(canEnterUtility)
  return (
    // 已锁定的批量操作快照；分页只分屏展示本次确认内容，不重新查询业务列表。
    <Table<Bill>
      rowKey="id"
      size="small"
      tableLayout="fixed"
      dataSource={bills}
      pagination={{ current, pageSize: 10, showSizeChanger: false, hideOnSinglePage: true, onChange: onPageChange }}
      scroll={{ x: 'max-content' }}
      columns={[
        { title: 'ID', dataIndex: 'id', width: '3.5rem' },
        { title: '账单号', dataIndex: 'billNo', width: '12rem' },
        { title: '订单号', dataIndex: 'sourceOrderNo', width: '12rem', render: (value) => value || '—' },
        {
          title: '业务类型',
          key: 'business',
          width: '8rem',
          render: (_, bill) => {
            if (!bill.businessName) return '—'
            return <BusinessTag businessName={bill.businessName} businessColor={bill.businessColor} />
          }
        },
        {
          title: '费用类型',
          dataIndex: 'feeType',
          width: '6rem',
          render: (value: Bill['feeType']) => <Tag color={BILL_KIND_COLORS[value]}>{BILL_KINDS[value]}</Tag>
        },
        {
          title: '客户 / 收款主体',
          key: 'party',
          width: '14rem',
          render: (_, bill) => (
            <div className="min-w-0 space-y-1">
              <div className="truncate" title={bill.customerName}>
                {bill.customerName}
              </div>
              <div className="text-content-soft truncate text-xs" title={bill.organizationName}>
                {bill.organizationName}
              </div>
            </div>
          )
        },
        ...(showMetering
          ? [
              {
                title: '计费方式 / 用量',
                key: 'metering',
                width: '18.5rem',
                render: (_: unknown, bill: Bill) =>
                  canEnterUtility(bill) ? (
                    <BillUtilityFields
                      bill={bill}
                      entry={entries[bill.id] ?? initialIssueEntry(bill)}
                      onChange={(entry) => onEntryChange?.(bill.id, entry)}
                      disabled={disabled}
                    />
                  ) : bill.status === 'draft' ? (
                    '直接填金额'
                  ) : (
                    '—'
                  )
              }
            ]
          : []),
        amountColumn ?? {
          title: editAmount ? '修改后金额（元）' : '金额（元）',
          dataIndex: 'totalAmount',
          width: '10rem',
          fixed: 'right',
          align: 'right',
          render: (value: string | null, bill) => {
            const entry = entries[bill.id] ?? initialIssueEntry(bill)
            if (editAmount ? bill.status === 'voided' : !issue || bill.status !== 'draft') {
              return value ?? '待确认'
            }
            if (!editAmount && entry.mode === 'usage') {
              const amount = issueEntryAmount(bill, entry)
              return (
                <span aria-label={`账单 ${bill.id} 计算金额`}>
                  {amount === null ? '待填写用量' : `¥${formatAmount(amount)}`}
                </span>
              )
            }
            return (
              <InputNumber<string>
                aria-label={`账单 ${bill.id} ${BILL_KINDS[bill.feeType]}金额`}
                className="w-full!"
                stringMode
                min="0"
                max="9999999999999999.99"
                precision={2}
                controls={false}
                placeholder="请输入金额"
                prefix="¥"
                value={entry.amount}
                disabled={disabled}
                onChange={(amount) => onEntryChange?.(bill.id, { ...entry, amount })}
              />
            )
          }
        },
        ...extraColumns
      ]}
    />
  )
}
